import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { apiAuth } from '@/api/apiAuth'
import store, { ActionTypes, MutationTypes } from '@/store'
import { can } from '@/api/permessi'

// Lazy loading: ogni pagina viaggia in un chunk separato.
const DefaultLayout = () => import('@/layouts/DefaultLayout.vue')
const Login = () => import('@/views/pages/Login.vue')
const Logout = () => import('@/views/pages/Logout.vue')
const AuthCallback = () => import('@/views/pages/AuthCallback.vue')
const DashboardPage = () => import('@/views/dashboard/DashboardPage.vue')
const OrdersList = () => import('@/views/orders/OrdersList.vue')
const OrderDetail = () => import('@/views/orders/OrderDetail.vue')
const CustomersList = () => import('@/views/customers/CustomersList.vue')
const CommissionBrackets = () => import('@/views/brackets/CommissionBrackets.vue')
const AgentReport = () => import('@/views/reports/AgentReport.vue')
const UsersPage = () => import('@/views/admin/UsersPage.vue')
const CraneTypesPage = () => import('@/views/admin/CraneTypesPage.vue')

declare module 'vue-router' {
  interface RouteMeta {
    title?: string
    requiresAuth?: boolean
    /** Permesso richiesto (catalogo in api/config/permissions.php). */
    permission?: string
  }
}

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'home',
    component: DefaultLayout,
    redirect: '/dashboard',
    children: [
      {
        path: '/dashboard',
        name: 'dashboard',
        meta: { title: 'Dashboard', requiresAuth: true },
        component: DashboardPage,
      },

      // --- Ordini ---
      {
        path: '/orders',
        name: 'orders',
        meta: { title: 'Lista ordini', requiresAuth: true, permission: 'orders.view' },
        component: OrdersList,
      },
      {
        path: '/orders/new',
        name: 'order-new',
        meta: { title: 'Nuovo ordine', requiresAuth: true, permission: 'orders.create' },
        component: OrderDetail,
      },
      {
        path: '/orders/:id(\\d+)',
        name: 'order-detail',
        meta: { title: 'Ordine', requiresAuth: true, permission: 'orders.view' },
        component: OrderDetail,
        props: true,
      },

      // --- Clienti ---
      {
        path: '/customers',
        name: 'customers',
        meta: { title: 'Clienti', requiresAuth: true, permission: 'customers.view' },
        component: CustomersList,
      },

      // --- Report ---
      {
        path: '/reports/agents',
        name: 'report-agents',
        meta: { title: 'Report agenti', requiresAuth: true, permission: 'reports.agents' },
        component: AgentReport,
      },

      // --- Tabelle provvigioni: D = ufficiale, S = simulazione ---
      {
        path: '/commission-brackets/:type(D|S)',
        name: 'commission-brackets',
        meta: { title: 'Tabelle provvigioni', requiresAuth: true, permission: 'brackets.view' },
        component: CommissionBrackets,
        props: true,
      },

      // --- Amministrazione ---
      {
        path: '/admin/users',
        name: 'admin-users',
        meta: { title: 'Utenti', requiresAuth: true, permission: 'users.manage' },
        component: UsersPage,
      },
      {
        path: '/admin/crane-types',
        name: 'admin-crane-types',
        meta: { title: 'Tipi gru', requiresAuth: true, permission: 'crane_types.manage' },
        component: CraneTypesPage,
      },
    ],
  },
  { path: '/login', name: 'login', component: Login },
  { path: '/logout', name: 'logout', component: Logout },
  // Callback SSO: riceve il token dal backend dopo il login Microsoft/Google.
  { path: '/auth/callback', name: 'auth-callback', component: AuthCallback },
  { path: '/:pathMatch(.*)*', redirect: '/dashboard' },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 }
  },
})

/**
 * `meta.permission` assente = rotta aperta a tutti gli autenticati.
 * È solo ergonomia: l'autorizzazione vera la applica l'API.
 */
const rottaAmmessa = (permesso?: string): boolean => !permesso || can(permesso)

router.beforeEach(async (to) => {
  if (!to.meta.requiresAuth) {
    return true
  }

  if (!store.state.isAuthenticated) {
    return { name: 'login' }
  }

  // Il token si valida una sola volta per sessione di navigazione:
  // le rotte successive si fidano dello store (il 401 lo intercetta http.ts).
  if (store.state.tokenChecked) {
    return rottaAmmessa(to.meta.permission) ? true : { name: 'dashboard' }
  }

  try {
    const { valid, user } = await apiAuth.checkToken()
    if (!valid) {
      await store.dispatch(ActionTypes.LOGOUT)
      return { name: 'login', query: { error: 'session_expired' } }
    }

    store.commit(MutationTypes.SET_TOKEN_CHECKED, true)
    if (user) {
      // I permessi si leggono DOPO checkToken: è questa risposta ad aggiornarli.
      localStorage.setItem('user', JSON.stringify(user))
      store.commit(MutationTypes.SET_USER, user)
    }
    return rottaAmmessa(to.meta.permission) ? true : { name: 'dashboard' }
  } catch {
    return { name: 'login', query: { error: 'server_unreachable' } }
  }
})

export default router
